import React, { useState, useEffect } from "react";
import { X, PlusCircle, Trash2 } from "lucide-react";

const CategoryForm = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: "",
    image: null,
    customProductPrice: 0,
    price: "",
    description: {
      title: "",
      features: [], // Initialize as empty array
      size: {
        width: "",
        height: "",
      },
    },
    discounts: [],
    sizes: [],
    additionalFeatures: [],
  });

  const [previewImage, setPreviewImage] = useState(null);

  // Preload data if editing an existing category
  useEffect(() => {
    if (initialData) {
      let parsedDescription = initialData.description;
      // Handle case where description is a string
      if (typeof initialData.description === "string") {
        try {
          parsedDescription = JSON.parse(initialData.description);
        } catch (e) {
          console.error("Failed to parse description:", e);
          parsedDescription = {
            title: "",
            features: [],
            size: { width: "", height: "" },
          };
        }
      }

      setFormData({
        name: initialData.name || "",
        image: initialData.image || null,
        customProductPrice: initialData.customProductPrice || 0,
        price: initialData.price ?? "",
        description: {
          title: parsedDescription?.title || "",
          features: Array.isArray(parsedDescription?.features)
            ? parsedDescription.features
            : [],
          size: {
            width: parsedDescription?.size?.width || "",
            height: parsedDescription?.size?.height || "",
          },
        },
        discounts: Array.isArray(initialData.promotion?.discounts)
          ? initialData.promotion.discounts
          : [],
        sizes: Array.isArray(initialData.sizes) ? initialData.sizes : [],
        additionalFeatures: Array.isArray(initialData.additionalFeatures)
          ? initialData.additionalFeatures
          : [],
      });
      setPreviewImage(initialData.image || null);
    } else {
      // Reset form to initial state
      setFormData({
        name: "",
        image: null,
        customProductPrice: 0,
        price: "",
        description: {
          title: "",
          features: [],
          size: { width: "", height: "" },
        },
        discounts: [],
        sizes: [],
        additionalFeatures: [],
      });
      setPreviewImage(null);
    }
  }, [initialData, isOpen]);

  // Generic row helpers for sizes / additionalFeatures
  const handleAddRow = (field, emptyRow) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), emptyRow],
    }));
  };

  const handleRowChange = (field, index, key, value) => {
    setFormData((prev) => {
      const rows = [...(prev[field] || [])];
      rows[index] = { ...rows[index], [key]: value };
      return { ...prev, [field]: rows };
    });
  };

  const handleRemoveRow = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index),
    }));
  };

  // Rest of the component remains the same...
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      description: {
        ...prev.description,
        [field]: value,
      },
    }));
  };

  const handleSizeChange = (dimension, value) => {
    setFormData((prev) => ({
      ...prev,
      description: {
        ...prev.description,
        size: {
          ...prev.description.size,
          [dimension]: value,
        },
      },
    }));
  };

  const handleAddFeature = () => {
    setFormData((prev) => ({
      ...prev,
      description: {
        ...prev.description,
        features: [...(prev.description.features || []), ""],
      },
    }));
  };

  const handleFeatureChange = (index, value) => {
    setFormData((prev) => {
      const updatedFeatures = [...(prev.description.features || [])];
      updatedFeatures[index] = value;
      return {
        ...prev,
        description: { ...prev.description, features: updatedFeatures },
      };
    });
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      description: {
        ...prev.description,
        features: (prev.description.features || []).filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      image: file,
    }));

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddDiscount = () => {
    setFormData((prev) => ({
      ...prev,
      discounts: [
        ...(prev.discounts || []),
        { buyX: 0, discountOnNext: 0, maxApplications: 0 },
      ],
    }));
  };

  const handleDiscountChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedDiscounts = [...(prev.discounts || [])];
      updatedDiscounts[index][field] = Number(value);
      return { ...prev, discounts: updatedDiscounts };
    });
  };

  const handleRemoveDiscount = (index) => {
    setFormData((prev) => ({
      ...prev,
      discounts: (prev.discounts || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("customProductPrice", formData.customProductPrice);
    if (formData.price !== "") {
      submitData.append("price", formData.price);
    }
    if (formData.image) {
      submitData.append("image", formData.image);
    }
    submitData.append("description", JSON.stringify(formData.description));
    submitData.append(
      "promotion",
      JSON.stringify({ discounts: formData.discounts || [] })
    );
    submitData.append(
      "sizes",
      JSON.stringify(
        (formData.sizes || [])
          .filter((s) => s.size && s.price !== "" && s.price !== null)
          .map((s) => ({ size: s.size, price: Number(s.price) }))
      )
    );
    submitData.append(
      "additionalFeatures",
      JSON.stringify(
        (formData.additionalFeatures || [])
          .filter((f) => f.feature && f.price !== "" && f.price !== null)
          .map((f) => ({ feature: f.feature, price: Number(f.price) }))
      )
    );

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
        >
          <X size={24} />
        </button>
        <h2 className="text-xl font-bold mb-4">
          {initialData ? "Edit Category" : "Add New Category"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Name Input */}
          <div>
            <label className="block text-gray-700 mb-2">Category Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter category name"
              required
            />
          </div>

          {/* Description Section */}
          <div>
            <label className="block text-gray-700 mb-2">Description</label>
            <div className="space-y-2">
              <input
                type="text"
                value={formData.description.title}
                onChange={(e) =>
                  handleDescriptionChange("title", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description title"
              />

              {/* Features Section */}
              <div>
                <label className="block text-gray-700 text-sm">Features</label>
                {(formData.description.features || []).map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) =>
                        handleFeatureChange(index, e.target.value)
                      }
                      className="flex-grow px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Feature ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddFeature}
                  className="flex items-center text-blue-500 hover:text-blue-700"
                >
                  <PlusCircle size={20} className="mr-1" /> Add Feature
                </button>
              </div>

              {/* Size Section */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-gray-700 text-sm">Width</label>
                  <input
                    type="text"
                    value={formData.description.size.width}
                    onChange={(e) => handleSizeChange("width", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Width"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm">Height</label>
                  <input
                    type="text"
                    value={formData.description.size.height}
                    onChange={(e) => handleSizeChange("height", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Height"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-gray-700 mb-2">Category Image</label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                onChange={handleFileChange}
                className="flex-grow px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                accept="image/*"
              />
              {previewImage && (
                <div className="w-20 h-20 rounded-lg overflow-hidden">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Products Price (applied to every product in this category) */}
          <div>
            <label className="block text-gray-700 mb-2">Products Price</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Set price for ALL products in this category"
              min="0"
              step="any"
            />
            <p className="text-xs text-gray-500 mt-1">
              Changing this updates the price of every product in this
              category.
            </p>
          </div>

          {/* Custom Product Price */}
          <div>
            <label className="block text-gray-700 mb-2">
              Custom Product Price
            </label>
            <input
              type="number"
              name="customProductPrice"
              value={formData.customProductPrice}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter custom product price"
              min="0"
            />
          </div>

          {/* Sizes Section (size name + price, shown on product cards/details) */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700">Sizes</label>
              <button
                type="button"
                onClick={() => handleAddRow("sizes", { size: "", price: "" })}
                className="flex items-center text-blue-500 hover:text-blue-700"
              >
                <PlusCircle size={20} className="mr-1" /> Add Size
              </button>
            </div>
            {(formData.sizes || []).map((row, index) => (
              <div key={index} className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  value={row.size}
                  onChange={(e) =>
                    handleRowChange("sizes", index, "size", e.target.value)
                  }
                  className="flex-grow px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Size (e.g. 10x10 cm)"
                />
                <input
                  type="number"
                  value={row.price}
                  onChange={(e) =>
                    handleRowChange("sizes", index, "price", e.target.value)
                  }
                  className="w-28 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Price"
                  min="0"
                  step="any"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveRow("sizes", index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
          </div>

          {/* Additional Features Section (feature name + price) */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700">Additional Features</label>
              <button
                type="button"
                onClick={() =>
                  handleAddRow("additionalFeatures", {
                    feature: "",
                    price: "",
                  })
                }
                className="flex items-center text-blue-500 hover:text-blue-700"
              >
                <PlusCircle size={20} className="mr-1" /> Add Feature
              </button>
            </div>
            {(formData.additionalFeatures || []).map((row, index) => (
              <div key={index} className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  value={row.feature}
                  onChange={(e) =>
                    handleRowChange(
                      "additionalFeatures",
                      index,
                      "feature",
                      e.target.value
                    )
                  }
                  className="flex-grow px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Feature (e.g. Lamination)"
                />
                <input
                  type="number"
                  value={row.price}
                  onChange={(e) =>
                    handleRowChange(
                      "additionalFeatures",
                      index,
                      "price",
                      e.target.value
                    )
                  }
                  className="w-28 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Price"
                  min="0"
                  step="any"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveRow("additionalFeatures", index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
          </div>

          {/* Discounts Section */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700">Discounts</label>
              <button
                type="button"
                onClick={handleAddDiscount}
                className="flex items-center text-blue-500 hover:text-blue-700"
              >
                <PlusCircle size={20} className="mr-1" /> Add Discount
              </button>
            </div>

            {(formData.discounts || []).map((discount, index) => (
              <div
                key={index}
                className="mb-2 border p-3 rounded bg-gray-50 relative"
              >
                <button
                  type="button"
                  onClick={() => handleRemoveDiscount(index)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                >
                  <Trash2 size={20} />
                </button>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-gray-600 text-sm">Buy X</label>
                    <input
                      type="number"
                      value={discount.buyX}
                      onChange={(e) =>
                        handleDiscountChange(index, "buyX", e.target.value)
                      }
                      className="w-full px-2 py-1 border rounded-lg text-sm"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm">
                      Discount %
                    </label>
                    <input
                      type="number"
                      value={discount.discountOnNext}
                      onChange={(e) =>
                        handleDiscountChange(
                          index,
                          "discountOnNext",
                          e.target.value
                        )
                      }
                      className="w-full px-2 py-1 border rounded-lg text-sm"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm">
                      Max Uses
                    </label>
                    <input
                      type="number"
                      value={discount.maxApplications}
                      onChange={(e) =>
                        handleDiscountChange(
                          index,
                          "maxApplications",
                          e.target.value
                        )
                      }
                      className="w-full px-2 py-1 border rounded-lg text-sm"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {initialData ? "Update" : "Add"} Category
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm;
